import { Type, type Static } from '@sinclair/typebox';
import { IsoDateSchema } from '../../shared/utils/date-time.schema';
import { StringEnum } from '../../utils/typebox';

export const CalendarEventAttendeeSchema = Type.Object(
  {
    email: Type.String({ description: 'Email address of the attendee.' }),
    display_name: Type.Optional(Type.String({ description: 'Display name of the attendee.' })),
    comment: Type.Optional(Type.String({ description: 'The response comment of the attendee.' })),
    is_organizer: Type.Boolean({ description: 'Is the attendee the organizer of the event.' }),
    is_self: Type.Boolean({ description: 'Is the attendee the connected account.' }),
    is_optional: Type.Boolean({ description: 'Is the attendee optional (based on type).' }),
    is_resource: Type.Boolean({ description: 'Is the attendee a resource (based on type).' }),
    type: StringEnum(['required', 'optional', 'resource'], {
      description: `Type of the attendee.
          - \`required\` is a required attendee.
          - \`optional\` is an optional attendee.
          - \`resource\` is a resource attendee like a room or a device.`,
    }),
    response_status: StringEnum(['yes', 'no', 'maybe', 'noreply'], {
      description: `The response status of the attendee.
          - \`yes\` if the invitation is accepted.
          - \`no\` if the invitation is declined.
          - \`maybe\` if the attendee is not sure.
          - \`noreply\` if the invitation is pending.`,
    }),
  },
  { description: 'An attendee of a calendar event.' },
);

export const CalendarEventDateTimeSchema = Type.Object(
  {
    type: Type.Literal('datetime', { description: 'Indicates a date-time value.' }),
    date_time: Type.String({
      description:
        'The date and time. May include a UTC offset, or be expressed as a local date and time without an offset (e.g. `2026-10-08T10:00:00`). When no offset is provided, the value is interpreted in `timezone`, or in the event-level timezone when set; otherwise it defaults to UTC.',
    }),
    timezone: Type.Optional(
      Type.String({
        description:
          'The IANA timezone in which the date and time is expressed (e.g. `Europe/Paris`). When omitted, uses the event-level timezone if set; otherwise defaults to UTC.',
      }),
    ),
  },
  { description: 'A date and time.', additionalProperties: false },
);

export const CalendarEventDateSchema = Type.Object(
  {
    type: Type.Literal('date', { description: 'Indicates a date-only value (for all-day events).' }),
    date: IsoDateSchema({ description: 'The date.' }),
    timezone: Type.Optional(
      Type.String({
        description: 'The IANA timezone in which the date is expressed (e.g. `Europe/Paris`).',
      }),
    ),
  },
  { description: 'A date.', additionalProperties: false },
);

export const CalendarEventConferenceSchema = Type.Object({
  provider: StringEnum(['google_meet', 'zoom', 'skype', 'teams', 'unknown'], {
    description: `The conference provider.
        - \`google_meet\` is a Google Meet conference.
        - \`zoom\` is a Zoom conference.
        - \`skype\` is a Skype conference.
        - \`teams\` is a Microsoft Teams conference.
        - \`unknown\` is an unknown conference provider.`,
  }),
  hosting: StringEnum(['native', 'description'], {
    description: `How the conference is attached to the event.
        - \`native\`: hosted by the calendar provider (Microsoft Teams or Skype on Outlook, Google Meet on Google). The
          provider owns the meeting and \`url\` is its join link.
        - \`description\`: an existing conference attached by URL, whose join link is published in the event body.`,
  }),
  conference_id: Type.Optional(Type.String({ description: 'The conference identifier assigned by its provider.' })),
  url: Type.String({ description: 'The URL used to join the conference.' }),
});

export const CalendarEventConferenceRequestSchema = Type.Object(
  {
    provider: StringEnum(['google_meet', 'zoom', 'skype', 'teams', 'unknown'], {
      description: 'The conference provider to generate or attach.',
    }),
    conference_id: Type.Optional(Type.String({ description: 'The conference identifier assigned by its provider.' })),
    url: Type.Optional(
      Type.String({
        description: `The URL of an existing conference to attach to the event. Must be an absolute credential-free HTTPS URL.
        When omitted, conference generation is requested. Availability depends on the calendar and the selected provider;
        when generation is not supported, provide the URL of an existing conference.`,
      }),
    ),
  },
  {
    description: `A conference to generate or attach to the event.
        The way the conference is associated with the event may vary depending on the calendar and the selected provider:
        it can be generated and hosted natively by the provider (e.g. Google Meet on Google, Microsoft Teams or Skype on
        Outlook), or, when a "url" is provided, attached as an existing conference whose join link is published in the
        event body.`,
    additionalProperties: false,
  },
);

export const CalendarEventSchema = Type.Object({
  object: Type.Literal('CalendarEvent'),
  id: Type.String({ description: 'The ID of the calendar event.' }),
  master_event_id: Type.Optional(Type.String({ description: 'If event instance, the ID of the master event.' })),
  calendar_id: Type.String({ description: 'The ID of the calendar the event belongs to.' }),
  ical_uid: Type.Optional(
    Type.String({
      description:
        'The iCalendar UID (RFC5545 UID) of the event. Unlike `id`, it is shared by every copy of the event across the calendars of its attendees, and stays stable when the event is exported or imported.',
    }),
  ),
  created_at: Type.String({
    description: 'The date the event was created. Uses ISO 8601 UTC datetime (YYYY-MM-DDTHH:MM:SSZ).',
  }),
  updated_at: Type.String({
    description: 'The date the event was last updated. Uses ISO 8601 UTC datetime (YYYY-MM-DDTHH:MM:SS.sssZ).',
  }),
  title: Type.String({ description: 'The title of the event.' }),
  body: Type.Optional(Type.String({ description: 'The body of the event.' })),
  location: Type.Optional(Type.String({ description: 'The location of the event.' })),
  is_cancelled: Type.Boolean({ description: 'Is the event cancelled.' }),
  is_all_day: Type.Boolean({ description: 'Is the event all day.' }),
  is_attendees_list_hidden: Type.Boolean({ description: 'Is the attendees list hidden for attendees.' }),
  attendees: Type.Optional(Type.Array(CalendarEventAttendeeSchema, { description: 'The attendees of the event.' })),
  start: Type.Union([CalendarEventDateTimeSchema, CalendarEventDateSchema], {
    description: 'The start date and time of the event.',
    discriminator: { propertyName: 'type' },
  }),
  end: Type.Union([CalendarEventDateTimeSchema, CalendarEventDateSchema], {
    description: 'The end date and time of the event.',
    discriminator: { propertyName: 'type' },
  }),
  recurrence: Type.Optional(
    Type.Array(Type.String(), {
      description: 'List of RRULE, EXRULE, RDATE and EXDATE lines for a recurring event, as specified in RFC5545.',
    }),
  ),
  organizer: Type.Object({
    email: Type.String({ description: 'Email address of the organizer.' }),
    display_name: Type.Optional(Type.String({ description: 'Display name of the organizer.' })),
  }),
  conference: Type.Optional(CalendarEventConferenceSchema),
  visibility: StringEnum(['public', 'private'], {
    description: `The visibility of the event.
        - \`public\` is visible to all.
        - \`private\` is visible only to the calendar owner.`,
  }),
  transparency: StringEnum(['opaque', 'transparent'], {
    description: `The transparency of the event.
        - \`opaque\` does block time on the calendar and is equivalent to setting Show me as to Busy in the Calendar UI.
        - \`transparent\` does not block time on the calendar and is equivalent to setting Show me as to Available in the Calendar UI.`,
  }),
  event_type: Type.String({
    description: 'The type of the event (`birthday`, `fromGmail`, `outOfOffice`...)',
    default: 'default',
  }),
  timezone: Type.Optional(
    Type.String({
      description:
        'The IANA timezone when start and end share the same timezone (e.g. `Europe/Paris`). Omitted when they differ; use `start.timezone` and `end.timezone` instead.',
    }),
  ),
  background_color: Type.Optional(Type.String({ description: 'Background color of the calendar in hexadecimal format.' })),
  text_color: Type.Optional(Type.String({ description: 'Foreground color of the event in hexadecimal format.' })),
  specifics: Type.Optional(Type.Any({ description: 'Object containing provider-specific calendar event data.' })),
});

export type CalendarEvent<Specifics = unknown> = Omit<Static<typeof CalendarEventSchema>, 'specifics'> & {
  specifics?: Specifics;
};
export type CalendarEventAttendee = Static<typeof CalendarEventAttendeeSchema>;
export type CalendarEventDate = Static<typeof CalendarEventDateSchema>;
export type CalendarEventDateTime = Static<typeof CalendarEventDateTimeSchema>;
export type CalendarEventConference = Static<typeof CalendarEventConferenceSchema>;
export type CalendarEventConferenceRequest = Static<typeof CalendarEventConferenceRequestSchema>;
